import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { buildLoginPath, consumeExplicitLogout } from "@/lib/loginRedirect";
import { toast } from "sonner";

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatQuantity(item) {
  return `${Number(item?.quantity || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} ${item?.unit || ""}`.trim();
}

export default function VendorOrders() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      if (consumeExplicitLogout()) {
        navigate("/", { replace: true });
        return;
      }
      navigate(buildLoginPath("/vendor-orders"), { replace: true });
      return;
    }
    if (!loading && user?.role === "admin") {
      navigate("/admin-dashboard");
      return;
    }
    if (!loading && user && user.role !== "vendor") {
      navigate("/orders");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role !== "vendor") return;
    setLoadingOrders(true);
    api.get("/orders/vendor")
      .then(({ data }) => setOrders(data || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoadingOrders(false));
  }, [user?.role]);

  const totals = useMemo(() => {
    return orders.reduce((acc, order) => {
      acc.orders += 1;
      acc.items += order.items?.length || 0;
      acc.total += Number(order.vendor_total || 0);
      return acc;
    }, { orders: 0, items: 0, total: 0 });
  }, [orders]);

  if (loading || !user || user.role !== "vendor") {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="py-32 text-center text-[#4B5563]">Loading vendor orders...</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="vendor-orders-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-label">Vendor</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-[#111827] mt-2 tracking-tight">
              Vendor Orders
            </h1>
            <p className="text-[#4B5563] mt-3 max-w-xl">
              Orders containing items assigned to {user.company || user.name}.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Orders Received</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{totals.orders}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Line Items</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{totals.items}</div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Vendor Total</div>
            <div className="text-2xl font-bold text-[#111827] mt-1">{formatMoney(totals.total)}</div>
          </div>
        </div>

        {loadingOrders ? (
          <div className="py-16 text-center text-[#4B5563]">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="border border-gray-200 rounded-xl p-10 text-center text-[#4B5563]">
            No vendor orders received yet.
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((order) => (
              <div key={order.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex flex-wrap justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">Order #{order.id.slice(0, 8)}</div>
                    <div className="text-sm text-[#111827] mt-1">{new Date(order.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block text-xs px-2 py-1 rounded-full ${order.payment_status === "done" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {order.payment_status === "done" ? "Payment Done" : "Payment Pending"}
                    </span>
                    <div className="text-xs text-[#4B5563] mt-2">Vendor Total</div>
                    <div className="font-display font-bold text-xl text-[#111827]">{formatMoney(order.vendor_total)}</div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[#111827]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Product</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Size</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Brand</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Location</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Quantity</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider">Unit Price</th>
                        <th className="text-right py-3 px-4 text-xs uppercase tracking-wider">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.items || []).map((item, idx) => (
                        <tr key={item.id || `${order.id}-${idx}`} className="border-t border-gray-100">
                          <td className="py-3 px-4 font-semibold text-[#111827]">{item.product_name}</td>
                          <td className="py-3 px-4 text-[#4B5563]">{item.size}</td>
                          <td className="py-3 px-4 text-[#4B5563]">{item.brand}</td>
                          <td className="py-3 px-4 text-[#4B5563]">{item.location || "-"}</td>
                          <td className="py-3 px-4 text-[#111827]">{formatQuantity(item)}</td>
                          <td className="py-3 px-4 text-[#111827]">{formatMoney(item.price)}</td>
                          <td className="py-3 px-4 text-right font-semibold text-[#111827]">{formatMoney(item.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 md:px-6 py-4 border-t border-gray-100 bg-[#FDFBF7]">
                  <div className="max-w-sm ml-auto space-y-1 text-sm">
                    <div className="flex justify-between gap-4">
                      <span className="text-[#4B5563]">Vendor Subtotal</span>
                      <span className="font-semibold text-[#111827]">{formatMoney(order.vendor_subtotal)}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-[#4B5563]">GST (18%)</span>
                      <span className="font-semibold text-[#111827]">{formatMoney(order.vendor_gst)}</span>
                    </div>
                    <div className="flex justify-between gap-4 pt-2 border-t border-[#C8A96A]/30">
                      <span className="font-semibold text-[#111827]">Vendor Total</span>
                      <span className="font-display font-bold text-[#C8A96A]">{formatMoney(order.vendor_total)}</span>
                    </div>
                    <div className="flex justify-between gap-4 text-xs text-[#4B5563]">
                      <span>Full Order Total</span>
                      <span>{formatMoney(order.order_total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
